import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {
  EnabledStatus,
  IB2BGiftCardListItem,
  IClaimCodeItem,
  ICreateGiftBookingPayload,
  ICreateOrUpdateB2BGiftCard,
  ICreateOrUpdateGiftCard,
  ICreateOrUpdateItem,
  IGiftCardDetails,
  IGiftCardLiteItem,
  IGiftCardsListItem,
  ItemsArrayType,
  ITransactionFieldItem,
  ListResponse,
} from '@/models';
import {changeEnabledStatus, http} from '@/utils';
import {handleApiCallInStore, updateItemById} from './utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface IGiftCardsStore {
  claimCodes: IClaimCodeItem[];
  B2BGiftCardTransactions: ITransactionFieldItem[];
  B2BTransactionsNbHits: number;
  allGiftCardsLite: IGiftCardLiteItem[];
  giftCardsList: IGiftCardsListItem[];
  giftCardsBookingList: ITransactionFieldItem[];
  B2BGiftCardsList: IB2BGiftCardListItem[];
  B2BGiftCardsNbHits: number;
  giftCardsNbHits: number;
  giftCardsBookingListNbHits: number;
  giftCardDetails: IGiftCardDetails;
  getGiftCardsList: (params) => void;
  getGiftBookingList: (params) => void;
  getB2BTransactionList: (params, id) => void;
  getB2BGiftCardList: (params) => void;
  changeGiftCardStatus: (payload: EnabledStatus) => void;
  getGiftCardById: (id, onSuccess: (data: IGiftCardDetails) => void) => void;
  getB2BGiftCardById: (
    id,
    onSuccess: (data: IB2BGiftCardListItem) => void
  ) => void;
  getClaimCodes: (id: string) => void;
  getAllGiftCardsLite: () => void;
  createOrUpdateGiftCard: (
    data: ICreateOrUpdateItem<ICreateOrUpdateGiftCard>
  ) => void;
  createOrUpdateB2BGiftCard: (
    data: ICreateOrUpdateItem<ICreateOrUpdateB2BGiftCard>
  ) => void;

  createGiftBooking: (
    payload: ICreateGiftBookingPayload,
    onSuccess: () => void
  ) => void;
}

export const useGiftCardsStore = create(
  persist<IGiftCardsStore>(
    set => ({
      B2BGiftCardTransactions: [],
      B2BTransactionsNbHits: 0,
      claimCodes: [],
      allGiftCardsLite: [],
      giftCardsList: [],
      B2BGiftCardsList: [],
      B2BGiftCardsNbHits: 0,
      giftCardsNbHits: 0,
      giftCardsBookingList: [],
      giftCardsBookingListNbHits: 0,
      giftCardDetails: undefined,
      getGiftCardsList: async params => {
        handleApiCallInStore(LoadingKeys.GetAllGiftCards, async () => {
          const {data} = await http.get<ListResponse<IGiftCardsListItem>>(
            API_ENDPOINTS.giftCards.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            giftCardsList: items ?? [],
            giftCardsNbHits: nbHits,
          }));
        });
      },
      getGiftBookingList: async params => {
        handleApiCallInStore(LoadingKeys.GiftBookingList, async () => {
          const {data} = await http.get<ListResponse<ITransactionFieldItem>>(
            API_ENDPOINTS.giftCards.booking,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            giftCardsBookingList: items ?? [],
            giftCardsBookingListNbHits: nbHits,
          }));
        });
      },
      getB2BTransactionList: async (params, id) => {
        handleApiCallInStore(LoadingKeys.GetB2BTransactionList, async () => {
          const {data} = await http.get<ListResponse<ITransactionFieldItem>>(
            `${API_ENDPOINTS.giftCards.b2b}/${id}/transactions`,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            B2BGiftCardTransactions: items ?? [],
            B2BTransactionsNbHits: nbHits,
          }));
        });
      },
      changeGiftCardStatus: payload => {
        handleApiCallInStore(LoadingKeys.ChangeGiftCardStatus, async () => {
          const {isEnabled, id} = payload;
          await http.put(`${API_ENDPOINTS.giftCards.all}/${id}/deactivate`, {
            isEnabled,
          });

          set(({giftCardsList}) => ({
            giftCardsList: updateItemById(
              giftCardsList,
              'isEnabled',
              isEnabled,
              id
            ),
          }));

          changeEnabledStatus(isEnabled, t('giftCard'));
        });
      },
      getGiftCardById: (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetGiftCardById, async () => {
          const {data} = await http.get<IGiftCardDetails>(
            `${API_ENDPOINTS.giftCards.all}/${id}`
          );
          set(() => ({
            giftCardDetails: data,
          }));
          await onSuccess(data);
        });
      },
      getB2BGiftCardById: (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetB2BGiftCardById, async () => {
          const {data} = await http.get<IB2BGiftCardListItem>(
            `${API_ENDPOINTS.giftCards.b2b}/${id}`
          );
          await onSuccess(data);
        });
      },
      createOrUpdateGiftCard: ({id, payload, onSuccess}) => {
        handleApiCallInStore(LoadingKeys.createOrUpdateGiftCard, async () => {
          const apiLink = `${API_ENDPOINTS.giftCards.all}${id ? `/${id}` : ''}`;
          if (id) {
            await http.put(apiLink, payload);
          } else {
            await http.post(apiLink, payload);
          }
          await onSuccess();
        });
      },
      createOrUpdateB2BGiftCard: ({id, payload, onSuccess}) => {
        handleApiCallInStore(
          LoadingKeys.CreateOrUpdateB2BGiftCard,
          async () => {
            const apiLink = `${API_ENDPOINTS.giftCards.b2b}${
              id ? `/${id}` : ''
            }`;
            if (id) {
              await http.put(apiLink, payload);
            } else {
              await http.post(apiLink, payload);
            }
            await onSuccess();
          }
        );
      },
      getB2BGiftCardList: params => {
        handleApiCallInStore(LoadingKeys.B2BGiftCardList, async () => {
          const {data} = await http.get<ListResponse<IB2BGiftCardListItem>>(
            API_ENDPOINTS.giftCards.b2b,
            {
              params,
            } as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            B2BGiftCardsList: items ?? [],
            B2BGiftCardsNbHits: nbHits,
          }));
        });
      },
      getClaimCodes: id => {
        handleApiCallInStore(LoadingKeys.GetClaimCodes, async () => {
          const {data} = await http.get<ItemsArrayType<IClaimCodeItem>>(
            `${API_ENDPOINTS.giftCards.b2b}/${id}/claim-codes`
          );
          set(() => ({
            claimCodes: data?.items,
          }));
        });
      },
      getAllGiftCardsLite: () => {
        handleApiCallInStore(LoadingKeys.GetAllGiftCardsLite, async () => {
          const {data} = await http.get<ItemsArrayType<IGiftCardLiteItem>>(
            `${API_ENDPOINTS.giftCards.allLite}`
          );
          set(() => ({
            allGiftCardsLite: data?.items,
          }));
        });
      },
      createGiftBooking: (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.CreateGiftBooking, async () => {
          await http.post<ICreateGiftBookingPayload>(
            API_ENDPOINTS.giftCards.transaction,
            payload
          );
          await onSuccess();
        });
      },
    }),

    {
      name: 'gift-cards-storage',

    }
  )
);
